import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { apiCall, ENDPOINTS } from '../../config/api';
import { FiUpload, FiDownload, FiTrash2, FiFile, FiRefreshCw } from 'react-icons/fi';

interface UserFile {
  name: string;
  size: number;
  last_modified: string | null;
  content_type: string;
}

const formatSize = (bytes: number) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
};

const UserFilesSidebarPanel: React.FC = () => {
  const { token } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<UserFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await apiCall(ENDPOINTS.USER_FILES.LIST, { method: 'GET' }, token);
      if (res.ok) setFiles(await res.json());
    } catch {
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files;
    if (!selected || !token) return;
    setUploading(true);
    try {
      const formData = new FormData();
      Array.from(selected).forEach(f => formData.append('file', f));
      const res = await apiCall(ENDPOINTS.USER_FILES.UPLOAD, { method: 'POST', body: formData }, token);
      if (res.ok) await load();
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDownload = async (file: UserFile) => {
    if (!token) return;
    const res = await apiCall(ENDPOINTS.USER_FILES.DOWNLOAD(file.name), { method: 'GET' }, token);
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.name;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDelete = async (file: UserFile) => {
    if (!token || !window.confirm(`Delete "${file.name}"?`)) return;
    await apiCall(ENDPOINTS.USER_FILES.DELETE(file.name), { method: 'DELETE' }, token);
    await load();
  };

  return (
    <div className="flex flex-col h-full">
      <div className="px-3 py-2 border-b border-base-300 flex-shrink-0 flex items-center gap-2">
        <button
          className="btn btn-xs btn-primary flex-1 gap-1"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          <FiUpload className="text-xs" />
          {uploading ? 'Uploading…' : 'Upload'}
        </button>
        <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleUpload} />
        <button className="btn btn-xs btn-ghost" onClick={load} title="Refresh">
          <FiRefreshCw className={`text-xs ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto divide-y divide-base-300">
        {loading && (
          <div className="flex justify-center py-8">
            <span className="loading loading-spinner loading-md" />
          </div>
        )}

        {!loading && files.length === 0 && (
          <div className="text-center text-base-content/40 py-10 px-4 text-sm">
            No files yet. Upload something!
          </div>
        )}

        {!loading && files.map(file => (
          <div key={file.name} className="px-3 py-2 flex items-center gap-2 hover:bg-base-200 group">
            <FiFile className="flex-shrink-0 text-base-content/40" />
            <div className="flex-1 min-w-0">
              <p className="text-sm truncate" title={file.name}>{file.name}</p>
              <p className="text-xs text-base-content/40">{formatSize(file.size)}</p>
            </div>
            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                className="btn btn-ghost btn-xs btn-square"
                onClick={() => handleDownload(file)}
                title="Download"
              >
                <FiDownload className="text-xs" />
              </button>
              <button
                className="btn btn-ghost btn-xs btn-square text-error"
                onClick={() => handleDelete(file)}
                title="Delete"
              >
                <FiTrash2 className="text-xs" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default UserFilesSidebarPanel;
